import React from 'react';

const frontMatterPattern = /^---[\s\S]*?---\s*/;
const inlinePattern = /(!?\[[^\]]+\]\([^)]+\)|`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g;
const commonTokenPattern = /\/\/.*|#.*|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\b\d+(?:\.\d+)?\b|\b[A-Za-z_][A-Za-z0-9_]*!?(?=\s*\()/g;

const keywords = {
  python: [
    'and', 'as', 'assert', 'break', 'class', 'continue', 'def', 'del', 'elif',
    'else', 'except', 'False', 'finally', 'for', 'from', 'if', 'import', 'in',
    'is', 'lambda', 'None', 'not', 'or', 'pass', 'raise', 'return', 'True',
    'try', 'while', 'with', 'yield'
  ],
  rust: [
    'as', 'break', 'const', 'continue', 'crate', 'else', 'enum', 'extern',
    'false', 'fn', 'for', 'if', 'impl', 'in', 'let', 'loop', 'match', 'mod',
    'move', 'mut', 'pub', 'ref', 'return', 'self', 'Self', 'static', 'struct',
    'super', 'trait', 'true', 'type', 'unsafe', 'use', 'where', 'while'
  ]
};

const keywordPatterns = Object.keys(keywords).reduce((patterns, language) => {
  patterns[language] = new RegExp(`\\b(${keywords[language].join('|')})\\b`, 'g');
  return patterns;
}, {});

const startsBlock = line => {
  return (
    /^#{1,6}\s+/.test(line) ||
    /^```/.test(line) ||
    /^>\s?/.test(line) ||
    /^[-*]\s+/.test(line) ||
    /^\d+\.\s+/.test(line)
  );
};

const parseLinkToken = token => {
  const image = token.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);

  if (image) {
    return {
      type: 'image',
      alt: image[1],
      href: image[2]
    };
  }

  const link = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);

  if (link) {
    return {
      type: 'link',
      text: link[1],
      href: link[2]
    };
  }

  return null;
};

const getTokenClassName = (token, language) => {
  const keywordPattern = keywordPatterns[language] || keywordPatterns.python;

  keywordPattern.lastIndex = 0;

  if (/^(\/\/|#)/.test(token)) {
    return 'token token-comment';
  }

  if (/^["']/.test(token)) {
    return 'token token-string';
  }

  if (/^\d/.test(token)) {
    return 'token token-number';
  }

  if (keywordPattern.test(token)) {
    return 'token token-keyword';
  }

  if (/^[A-Z][A-Za-z0-9_]*$/.test(token)) {
    return 'token token-type';
  }

  return 'token token-function';
};

const highlightCodeLine = (line, language, keyPrefix) => {
  const normalizedLanguage = language === 'rs' ? 'rust' : language;
  const keywordPattern = keywordPatterns[normalizedLanguage];
  const tokenPattern = keywordPattern
    ? new RegExp(`${commonTokenPattern.source}|${keywordPattern.source}`, 'g')
    : commonTokenPattern;
  const parts = [];
  let cursor = 0;
  let match;

  while ((match = tokenPattern.exec(line)) !== null) {
    const token = match[0];
    const index = match.index;

    if (index > cursor) {
      parts.push(line.slice(cursor, index));
    }

    parts.push(
      <span
        className={getTokenClassName(token, normalizedLanguage)}
        key={`${keyPrefix}-${index}`}
      >
        {token}
      </span>
    );

    cursor = index + token.length;
  }

  if (cursor < line.length) {
    parts.push(line.slice(cursor));
  }

  return parts;
};

const highlightCode = (code, language, keyPrefix) => {
  const lines = code.split('\n');
  const parts = [];

  lines.forEach((line, lineIndex) => {
    parts.push(...highlightCodeLine(line, language, `${keyPrefix}-${lineIndex}`));

    if (lineIndex < lines.length - 1) {
      parts.push('\n');
    }
  });

  return parts;
};

export const parseInlineMarkdown = (text, keyPrefix = 'inline') => {
  const parts = [];
  let cursor = 0;
  let match;

  inlinePattern.lastIndex = 0;

  while ((match = inlinePattern.exec(text)) !== null) {
    const token = match[0];
    const index = match.index;

    if (index > cursor) {
      parts.push(text.slice(cursor, index));
    }

    const linkToken = parseLinkToken(token);
    const key = `${keyPrefix}-${index}`;

    if (linkToken && linkToken.type === 'image') {
      parts.push(
        <img key={key} src={linkToken.href} alt={linkToken.alt} />
      );
    } else if (linkToken && linkToken.type === 'link') {
      parts.push(
        <a key={key} href={linkToken.href} target='_blank' rel='noreferrer'>
          {parseInlineMarkdown(linkToken.text, key)}
        </a>
      );
    } else if (token[0] === '`') {
      parts.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else if (token.indexOf('**') === 0) {
      parts.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token[0] === '*') {
      parts.push(<em key={key}>{token.slice(1, -1)}</em>);
    }

    cursor = index + token.length;
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  return parts;
};

export const renderMarkdown = markdown => {
  const cleanMarkdown = markdown.replace(frontMatterPattern, '').replace(/\r\n/g, '\n');
  const lines = cleanMarkdown.split('\n');
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      index += 1;
      continue;
    }

    const fence = line.match(/^```([A-Za-z0-9_-]+)?\s*$/);

    if (fence) {
      const language = fence[1] || '';
      const code = [];
      index += 1;

      while (index < lines.length && !/^```\s*$/.test(lines[index])) {
        code.push(lines[index]);
        index += 1;
      }

      index += 1;
      blocks.push(
        <pre key={`block-${blocks.length}`}>
          <code data-language={language}>
            {highlightCode(code.join('\n'), language, `code-${blocks.length}`)}
          </code>
        </pre>
      );
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.+)$/);

    if (heading) {
      const level = Math.min(heading[1].length, 6);
      const Tag = `h${level}`;

      blocks.push(
        <Tag key={`block-${blocks.length}`}>
          {parseInlineMarkdown(heading[2], `heading-${blocks.length}`)}
        </Tag>
      );
      index += 1;
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quote = [];

      while (index < lines.length && /^>\s?/.test(lines[index])) {
        quote.push(lines[index].replace(/^>\s?/, ''));
        index += 1;
      }

      blocks.push(
        <blockquote key={`block-${blocks.length}`}>
          <p>{parseInlineMarkdown(quote.join(' '), `quote-${blocks.length}`)}</p>
        </blockquote>
      );
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      const items = [];

      while (index < lines.length && /^[-*]\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^[-*]\s+/, ''));
        index += 1;
      }

      blocks.push(
        <ul key={`block-${blocks.length}`}>
          {items.map((item, itemIndex) => (
            <li key={itemIndex}>
              {parseInlineMarkdown(item, `ul-${blocks.length}-${itemIndex}`)}
            </li>
          ))}
        </ul>
      );
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items = [];

      while (index < lines.length && /^\d+\.\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^\d+\.\s+/, ''));
        index += 1;
      }

      blocks.push(
        <ol key={`block-${blocks.length}`}>
          {items.map((item, itemIndex) => (
            <li key={itemIndex}>
              {parseInlineMarkdown(item, `ol-${blocks.length}-${itemIndex}`)}
            </li>
          ))}
        </ol>
      );
      continue;
    }

    const paragraph = [];

    while (
      index < lines.length &&
      lines[index].trim() &&
      !startsBlock(lines[index])
    ) {
      paragraph.push(lines[index].trim());
      index += 1;
    }

    blocks.push(
      <p key={`block-${blocks.length}`}>
        {parseInlineMarkdown(paragraph.join(' '), `paragraph-${blocks.length}`)}
      </p>
    );
  }

  return blocks;
};
